import paywallVariantsConfig from "../config/paywallVariants.json";

const EASTERN_TIME_ZONE = "America/New_York";
const MARKET_OPEN_MINUTES = 9 * 60 + 30;
const MARKET_CLOSE_MINUTES = 16 * 60;

const hashString = (value) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash * 31) + value.charCodeAt(index)) % 1000000007;
  }

  return Math.abs(hash).toString(16);
};

export const PAYWALL_COPY_VERSION = paywallVariantsConfig.copyVersion;
export const PAYWALL_MODAL_BENEFITS = paywallVariantsConfig.modalBenefits || [];
export const PAYWALL_CONTEXT_VARIANTS = paywallVariantsConfig.variants || [];
export const PAYWALL_CONTENT_HASH = hashString(JSON.stringify(paywallVariantsConfig));
export const PAYWALL_SURFACE_TYPES = {
  dailyLimit: "daily_limit",
  freeQuestionLimit: "free_question_limit",
  featureGate: "feature_gate",
};

const WEEKEND_DAYS = new Set(["Sat", "Sun"]);

const easternDateParts = (date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN_TIME_ZONE,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const valueFor = (type) => parts.find((part) => part.type === type)?.value;
  const hour = Number(valueFor("hour") || 0);
  const minute = Number(valueFor("minute") || 0);

  return {
    weekday: valueFor("weekday") || "",
    year: Number(valueFor("year") || 0),
    month: Number(valueFor("month") || 0),
    day: Number(valueFor("day") || 0),
    hour,
    minute,
    minutesIntoDay: hour * 60 + minute,
  };
};

const dateKey = ({ year, month, day }) => (
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
);

const dateKeyFromUtcDate = (date) => dateKey({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth() + 1,
  day: date.getUTCDate(),
});

const observedFixedHolidayKey = ({ year, month, day }) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  const weekday = date.getUTCDay();

  if (weekday === 6) {
    date.setUTCDate(date.getUTCDate() - 1);
  } else if (weekday === 0) {
    date.setUTCDate(date.getUTCDate() + 1);
  }

  return dateKeyFromUtcDate(date);
};

const nthWeekdayOfMonthKey = ({ year, month, weekday, occurrence }) => {
  const date = new Date(Date.UTC(year, month - 1, 1));
  const offset = (weekday - date.getUTCDay() + 7) % 7;
  date.setUTCDate(1 + offset + (occurrence - 1) * 7);
  return dateKeyFromUtcDate(date);
};

const lastWeekdayOfMonthKey = ({ year, month, weekday }) => {
  const date = new Date(Date.UTC(year, month, 0));
  const offset = (date.getUTCDay() - weekday + 7) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return dateKeyFromUtcDate(date);
};

const easterSundayUtcDate = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(Date.UTC(year, month - 1, day));
};

const goodFridayKey = (year) => {
  const date = easterSundayUtcDate(year);
  date.setUTCDate(date.getUTCDate() - 2);
  return dateKeyFromUtcDate(date);
};

const usEquityMarketHolidayKeys = (year) => new Set([
  observedFixedHolidayKey({ year, month: 1, day: 1 }),
  observedFixedHolidayKey({ year: year + 1, month: 1, day: 1 }),
  nthWeekdayOfMonthKey({ year, month: 1, weekday: 1, occurrence: 3 }),
  nthWeekdayOfMonthKey({ year, month: 2, weekday: 1, occurrence: 3 }),
  goodFridayKey(year),
  lastWeekdayOfMonthKey({ year, month: 5, weekday: 1 }),
  observedFixedHolidayKey({ year, month: 6, day: 19 }),
  observedFixedHolidayKey({ year, month: 7, day: 4 }),
  nthWeekdayOfMonthKey({ year, month: 9, weekday: 1, occurrence: 1 }),
  nthWeekdayOfMonthKey({ year, month: 11, weekday: 4, occurrence: 4 }),
  observedFixedHolidayKey({ year, month: 12, day: 25 }),
]);

export const isUsEquityMarketHoliday = ({ year, month, day }) => (
  usEquityMarketHolidayKeys(year).has(dateKey({ year, month, day }))
);

export const formatPaywallDuration = (minutes) => {
  const safeMinutes = Math.max(Number(minutes) || 0, 0);
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;

  if (hours <= 0) {
    return `${safeMinutes} minute${safeMinutes === 1 ? "" : "s"}`;
  }

  if (remainder === 0) {
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }

  return `${hours} hour${hours === 1 ? "" : "s"} ${remainder} minute${remainder === 1 ? "" : "s"}`;
};

const buildMarketState = (now) => {
  const easternParts = easternDateParts(now);
  const minutesIntoDay = easternParts.minutesIntoDay;

  if (WEEKEND_DAYS.has(easternParts.weekday)) {
    return {
      marketState: "weekend",
      sessionKey: "weekend",
      phaseLabel: "Weekend",
      minutesIntoDay,
      easternWeekday: easternParts.weekday,
    };
  }

  if (isUsEquityMarketHoliday(easternParts)) {
    return {
      marketState: "market_holiday",
      sessionKey: "market_holiday",
      phaseLabel: "Market holiday",
      minutesIntoDay,
      easternWeekday: easternParts.weekday,
    };
  }

  if (minutesIntoDay < MARKET_OPEN_MINUTES) {
    return {
      marketState: "before_open",
      sessionKey: "before_open",
      phaseLabel: "Premarket",
      minutesToOpen: MARKET_OPEN_MINUTES - minutesIntoDay,
      minutesIntoDay,
      easternWeekday: easternParts.weekday,
    };
  }

  if (minutesIntoDay >= MARKET_CLOSE_MINUTES) {
    return {
      marketState: "after_close",
      sessionKey: "after_close",
      phaseLabel: "After hours",
      minutesSinceClose: minutesIntoDay - MARKET_CLOSE_MINUTES,
      minutesIntoDay,
      easternWeekday: easternParts.weekday,
    };
  }

  const minutesToClose = MARKET_CLOSE_MINUTES - minutesIntoDay;

  return {
    marketState: "regular_open",
    sessionKey: "regular_open",
    phaseLabel: easternParts.weekday === "Fri" ? "Friday regular session" : "Mid-session",
    minutesToClose,
    minutesAfterOpen: minutesIntoDay - MARKET_OPEN_MINUTES,
    minutesIntoDay,
    easternWeekday: easternParts.weekday,
  };
};

const variantPriority = (variant) => Number(variant.selection?.priority || 0);

const sortedVariants = () => (
  [...PAYWALL_CONTEXT_VARIANTS].sort((left, right) => variantPriority(right) - variantPriority(left))
);

const variantMatchesMarketState = (variant, state, assetFocus) => {
  const selection = variant.selection || {};
  if (selection.assetFocus && selection.assetFocus !== assetFocus) return false;
  if (selection.marketState && selection.marketState !== state.marketState) return false;
  if (selection.easternWeekday && selection.easternWeekday !== state.easternWeekday) return false;
  if (selection.minutesAfterOpenLte !== undefined && Number(state.minutesAfterOpen) > Number(selection.minutesAfterOpenLte)) return false;
  if (selection.minutesAfterOpenGte !== undefined && Number(state.minutesAfterOpen) < Number(selection.minutesAfterOpenGte)) return false;
  if (selection.minutesToCloseLte !== undefined && Number(state.minutesToClose) > Number(selection.minutesToCloseLte)) return false;
  if (selection.minutesToCloseGte !== undefined && Number(state.minutesToClose) < Number(selection.minutesToCloseGte)) return false;
  if (selection.minutesToOpenLte !== undefined && Number(state.minutesToOpen) > Number(selection.minutesToOpenLte)) return false;
  if (selection.minutesToOpenGte !== undefined && Number(state.minutesToOpen) < Number(selection.minutesToOpenGte)) return false;
  if (selection.defaultForMarketState && selection.marketState !== state.marketState) return false;
  return selection.assetFocus || selection.marketState || selection.default;
};

const variantFallback = () => (
  PAYWALL_CONTEXT_VARIANTS.find((variant) => variant.key === "general") ||
  PAYWALL_CONTEXT_VARIANTS[0] ||
  {}
);

const contextForVariant = (variant, state, { forcedVariantKey } = {}) => ({
  ...variant,
  sessionKey: variant.sessionKey || state.sessionKey,
  phaseLabel: state.marketState === "regular_open" && variant.key === "final_hour" && state.easternWeekday === "Fri"
    ? "Friday final hour"
    : variant.phaseLabel || state.phaseLabel,
  minutesToClose: state.minutesToClose ?? variant.minutesToClose,
  minutesToOpen: state.minutesToOpen ?? variant.minutesToOpen,
  minutesSinceClose: state.minutesSinceClose ?? variant.minutesSinceClose,
  minutesAfterOpen: state.minutesAfterOpen,
  minutesIntoDay: state.minutesIntoDay,
  marketState: state.marketState,
  easternWeekday: state.easternWeekday,
  forcedVariantKey,
});

export const resolvePaywallMarketContext = ({ now = new Date(), forcedVariantKey, assetFocus } = {}) => {
  const state = buildMarketState(now);

  if (forcedVariantKey) {
    const forcedVariant = PAYWALL_CONTEXT_VARIANTS.find((variant) => variant.key === forcedVariantKey);
    if (forcedVariant) return contextForVariant(forcedVariant, state, { forcedVariantKey });
  }

  const selectedVariant = sortedVariants().find((variant) => (
    variantMatchesMarketState(variant, state, assetFocus)
  )) || variantFallback();

  return contextForVariant(selectedVariant, state);
};

const upgradeActionText = ({ eligibleForProTrial, proTrialDays }) => (
  eligibleForProTrial
    ? `Start your ${proTrialDays}-day free trial`
    : "Upgrade to Incite Pro"
);

const renderTemplate = (template, values) => (
  String(template || "").replace(/\{([a-zA-Z0-9_]+)\}/g, (_match, key) => values[key] || "")
);

const dailyLimitDescription = ({ marketContext, eligibleForProTrial, proTrialDays }) => {
  const action = upgradeActionText({ eligibleForProTrial, proTrialDays });
  const keepUpdated = `${action} so Incite can keep you updated on catalysts, price action, and decision points in real time.`;
  const fallbackTemplate = "Do not miss what the market does next. {keepUpdated}";

  return renderTemplate(marketContext.descriptionTemplate || fallbackTemplate, {
    action,
    keepUpdated,
    durationToClose: formatPaywallDuration(marketContext.minutesToClose),
    durationToOpen: formatPaywallDuration(marketContext.minutesToOpen),
  });
};

const featureGateDescription = ({ eligibleForProTrial, proTrialDays }) => {
  const action = upgradeActionText({ eligibleForProTrial, proTrialDays });
  return renderTemplate(paywallVariantsConfig.featureGateDescriptionTemplate, { action });
};

export const buildPaywallModalContent = ({
  eligibleForProTrial = false,
  proTrialDays = 7,
  isDailyLimitPaywall = true,
  surfaceType,
  forcedVariantKey,
  assetFocus,
  now,
} = {}) => {
  const marketContext = resolvePaywallMarketContext({ now, forcedVariantKey, assetFocus });
  const resolvedSurfaceType = surfaceType || (
    isDailyLimitPaywall ? PAYWALL_SURFACE_TYPES.dailyLimit : PAYWALL_SURFACE_TYPES.featureGate
  );
  const descriptionText = isDailyLimitPaywall
    ? dailyLimitDescription({ marketContext, eligibleForProTrial, proTrialDays })
    : featureGateDescription({ eligibleForProTrial, proTrialDays });

  return {
    descriptionText,
    variantKey: isDailyLimitPaywall ? marketContext.key : "feature_gate",
    marketContext,
    modalBenefits: PAYWALL_MODAL_BENEFITS,
    trackingMetadata: {
      paywall_variant: isDailyLimitPaywall ? marketContext.key : "feature_gate",
      paywall_surface_type: resolvedSurfaceType,
      paywall_copy_version: PAYWALL_COPY_VERSION,
      paywall_content_hash: PAYWALL_CONTENT_HASH,
      market_session: marketContext.sessionKey,
      market_phase: marketContext.phaseLabel,
      market_minutes_to_close: marketContext.minutesToClose,
      market_minutes_to_open: marketContext.minutesToOpen,
      paywall_context_source: forcedVariantKey ? "forced_preview" : "client_time",
    },
  };
};
